import React, { FC, useEffect, useMemo } from 'react';
import './theme/add-event-button-theme.scss';
import { SmallArrowDownIcon, AddInactiveIcon } from '@getgo/chameleon-icons/react';
import classNames from 'classnames';
import format from 'date-fns/format';
import { useUserContext } from '../context/userContext';
import { getFormattedTimeZone } from '../utils/eventDataFormat';
import { Trans, useTranslation } from 'react-i18next';
import {
  onClickOfEventCalenderTracker,
  onClickOfRemindMeCalenderTracker,
} from '../../mixpanel/Feature/eventTracker';

export enum AddEventButtonTheme {
  default = '',
  tertiarySmall = 'tertiary-small',
}

export interface AddEventButtonProps {
  eventStartDate: Date;
  eventEndDate: Date;
  title: string;
  buttonName: string;
  theme?: AddEventButtonTheme;
  withDropdown?: boolean;
  timezone: string | undefined;
  isConferenceButton?: boolean;
  conferenceKey?: string | undefined;
  eventKey: string | undefined;
}

const formatEventTime = (date: Date) => format(date, 'yyyy-MM-dd HH:mm');

declare global {
  interface Window {
    addeventatc: any;
  }
}

export const AddEventButton: FC<AddEventButtonProps> = ({
  conferenceKey,
  eventStartDate,
  eventEndDate,
  title,
  buttonName,
  theme = AddEventButtonTheme.default,
  withDropdown = false,
  timezone,
  isConferenceButton = false,
  eventKey,
}) => {
  const { t } = useTranslation();
  const { userDetail } = useUserContext();

  useEffect(() => {
    window.addeventatc.refresh();
  }, []);

  const conferenceLink = useMemo(
    () => `${window.location.origin}/event/${eventKey}/${userDetail.eventRegistrationId}`,
    [userDetail.eventRegistrationId, eventKey],
  );

  const eventInfo = (
    <>
      <span className="start">{formatEventTime(eventStartDate)}</span>
      <span className="end">{formatEventTime(eventEndDate)}</span>
      <span className="timezone">{getFormattedTimeZone(timezone)}</span>
      <span className="title">GoTo Webinar - {title}</span>
      <span className="description">
        {isConferenceButton ? (
          <Trans i18nKey="addEventCalendar.sessionDescription">
            <p>
              To make the most of your event, be sure to use headphones and have a stable internet
              connection.
            </p>
            <a href={conferenceLink}>Go to your event page</a>
            <p>{`You'll connect with your computer audio (VoIP).`}</p>
            <p>Looking forward to it!</p>
            <p>Your Event Team</p>
            <a href="https://link.gotowebinar.com/confirmation?source=attendeeRegistrationPage&language=english">
              View system requirements
            </a>
          </Trans>
        ) : (
          <Trans i18nKey="addEventCalendar.eventDescription">
            <p>As we get closer to the big event, check out the upcoming sessions and speakers:</p>
            <a href={conferenceLink}>Go to your event page</a>
            <p>This is where you join the event.</p>
            <p>{`You'll connect to individual sessions with your computer audio (VoIP).`}</p>
            <p>Looking forward to it!</p>
            <p>Your Event Team</p>
            <a href="https://link.gotowebinar.com/confirmation?source=attendeeRegistrationPage&language=english">
              View system requirements
            </a>
          </Trans>
        )}
      </span>
      <span className="location">{t('addEventCalendar.calendarLocation')}</span>
    </>
  );
  const onAddEventButtonClick = (e: any) => {
    const calnderType = e.target.id.split('-')[1];
    if (calnderType) {
      if (conferenceKey) {
        onClickOfRemindMeCalenderTracker({ eventKey, conferenceKey, calnderType });
      } else {
        onClickOfEventCalenderTracker({ eventKey, calnderType });
      }
    } else if (!isConferenceButton && e.target.getAttribute('data-direct') === 'outlook') {
      onClickOfEventCalenderTracker({ eventKey, calnderType: 'outlook' });
    }
  };
  return (
    <div>
      <div id="custom-add-event" className={theme} onClick={(e) => onAddEventButtonClick(e)}>
        <div
          title={buttonName}
          className={classNames('addeventatc control default', {
            'addeventact-prefix': withDropdown,
          })}
          data-direct={!isConferenceButton && 'outlook'}
        >
          {buttonName}
          {isConferenceButton && (
            <span className="event-calendar-icon">
              <AddInactiveIcon />
            </span>
          )}
          {eventInfo}
        </div>
        {withDropdown && (
          <div
            title={t('event.registration.addEventDropDownTooltip')}
            className="addeventatc control default addeventact-button-suffix"
          >
            <SmallArrowDownIcon />
            {eventInfo}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddEventButton;
